import {useEffect, useMemo} from 'react';
import {create} from 'zustand';

import {
  RampIdType,
  useViewerFeatureFlagsQuery,
} from '../../../generated/graphql';
import {type FlagMap, toFlagMap} from './flagMap';

/**
 * Caches `useViewerFeatureFlagsQuery` results as O(1) rampKey -> boolean maps
 * (pre-evaluated at init time, one map per rampIDType). Readers do a single
 * Record lookup and no Datadog work.
 */
export type ViewerFlagsState = {
  flagsByUserName: FlagMap | null;
  flagsByUserId: FlagMap | null;
};

const INITIAL_VIEWER_FLAGS_STATE: ViewerFlagsState = {
  flagsByUserName: null,
  flagsByUserId: null,
};

// Reader hooks live in `useRampFlagStore.ts`; export the store so they can
// subscribe to it directly.
export const useViewerFlagsStore = create<ViewerFlagsState>()(
  () => INITIAL_VIEWER_FLAGS_STATE
);

export function useInitializeViewerFlagsStore() {
  const {data: dataUserName} = useViewerFeatureFlagsQuery({
    variables: {rampIDType: RampIdType.UserName},
  });
  const {data: dataUserId} = useViewerFeatureFlagsQuery({
    variables: {rampIDType: RampIdType.UserId},
  });

  const flagsByUserName = useMemo(
    () => toFlagMap(dataUserName?.viewer?.featureFlags ?? null),
    [dataUserName]
  );
  const flagsByUserId = useMemo(
    () => toFlagMap(dataUserId?.viewer?.featureFlags ?? null),
    [dataUserId]
  );

  // This is a valid Effect: it synchronizes React hook outputs into the
  // external Zustand store after render.
  // https://react.dev/reference/react/useEffect
  useEffect(() => {
    useViewerFlagsStore.setState({flagsByUserName, flagsByUserId});
  }, [flagsByUserName, flagsByUserId]);
}
